/**
 * laborHoursSuggestion — client-side EXIF-duration suggestion (spec 011
 * Slice 2, FR-3/FR-9).
 *
 * PARITY REFERENCE: `suggestDuration` below MUST match gg-app's
 * `crates/gg-shared/src/models.rs::suggest_duration` bit-for-bit. Any change
 * here must be ported there (and vice versa), or the client's prefill and
 * the server's acceptance of `LaborHoursSource::PhotoEstimated` diverge. The
 * server NEVER recomputes this — it only records the label the client
 * asserts (see `useAddServiceEntry.ts`) — so client/server drift here is a
 * silent correctness bug, not a loud one.
 *
 * Algorithm (mirrors the Rust fn exactly):
 *   1. Either capture timestamp missing/unparseable -> no suggestion.
 *   2. deltaMin = whole minutes, FLOORED: `Math.floor(deltaMs / 60000)`.
 *      Rust's `chrono::Duration::num_minutes()` truncates toward zero, which
 *      is identical to `Math.floor` for the positive deltas this function
 *      ever accepts (deltaMin <= 0 is rejected below) — do NOT reuse
 *      `Math.floor` as a truncate-toward-zero substitute for negative inputs
 *      elsewhere; it rounds the wrong direction there.
 *   3. No suggestion if `deltaMin <= 0` (swapped/identical) or
 *      `deltaMin > MAX_SESSION_MIN` (implausible session length).
 *   4. `hours = Math.round(deltaMin / 60 * 4) / 4` — nearest quarter hour.
 *      `Math.round` (half-up) equals Rust's `f64::round` (half-away-from-zero)
 *      ONLY because `deltaMin` is always positive here (step 3 guarantees
 *      it) — do not port this `Math.round` equivalence to a context with
 *      negative inputs.
 *   5. `hours === 0` (a sub-~7.5-minute delta) -> no suggestion; a zero-hour
 *      estimate is not useful.
 *
 * EXIF handling: `DateTimeOriginal` is naive local time, NO timezone
 * (format `YYYY:MM:DD HH:MM:SS`). Both timestamps are parsed identically and
 * their *fields* (not an absolute instant) are re-based onto a fixed
 * reference clock (UTC, via `Date.UTC`) purely so the delta arithmetic is
 * deterministic across the browser's local timezone / DST — no real
 * timezone conversion is ever applied, matching the "naive, no zone" EXIF
 * contract. A DST transition landing between the two shots is a negligible,
 * accepted edge case (same posture the spec calls out).
 *
 * Absolute capture-time is NEVER sent to the server or persisted anywhere —
 * only the resulting delta-derived `labor_hours` value + a provenance label
 * (`laborHoursSource`) ever leaves the browser (spec 011 Locked Decision 3).
 */

/** Maximum plausible before/after photo session length, in minutes (8h). */
export const MAX_SESSION_MIN = 480;

/**
 * Suggest a labour-hours duration from two capture timestamps (epoch ms,
 * already timezone-neutral — see `parseExifDateTimeOriginal`). Pure,
 * side-effect-free. THE PARITY REFERENCE — see file header.
 */
export function suggestDuration(
  beforeMs: number | null | undefined,
  afterMs: number | null | undefined,
): number | null {
  if (beforeMs === null || beforeMs === undefined) return null;
  if (afterMs === null || afterMs === undefined) return null;

  const deltaMin = Math.floor((afterMs - beforeMs) / 60_000);
  if (deltaMin <= 0 || deltaMin > MAX_SESSION_MIN) return null;

  const hours = Math.round((deltaMin / 60) * 4) / 4;
  if (hours === 0) return null;

  return hours;
}

const EXIF_DATETIME_RE = /^(\d{4}):(\d{2}):(\d{2})[ T](\d{2}):(\d{2}):(\d{2})$/;

/**
 * Parses an EXIF `DateTimeOriginal` value into a timezone-neutral epoch-ms
 * number, or `null` if unparseable/absent.
 *
 * Accepts either the raw EXIF string (`"YYYY:MM:DD HH:MM:SS"` — what exifr
 * returns when called with `reviveValues: false`, see `readExifCapturedAtMs`)
 * or a `Date` instance (defensive — some EXIF readers revive the tag into a
 * `Date` built from the naive fields via the RUNTIME's local timezone; this
 * branch reads those fields back out with the local getters and re-bases
 * them onto UTC, undoing that local-time interpretation so the delta math
 * matches the string path in the general case). This round-trip is NOT
 * exact for every input: if the naive local time falls in a DST
 * spring-forward gap (a wall-clock time that never occurs, e.g. 02:30 on
 * the day clocks jump from 02:00 to 03:00), the JS engine that built the
 * `Date` will have already shifted it to a real instant, and the local
 * getters read back the shifted fields, not the original naive ones — a
 * small, silent skew for that one photo. The primary string path above is
 * immune (it parses the naive fields directly into `Date.UTC`, no local
 * timezone is ever consulted); this caveat only applies to the defensive
 * `Date`-instance branch, and only for capture times inside the gap.
 *
 * `Date.UTC` is used only as a fixed, deterministic reference clock — it
 * does NOT assert the photo was actually taken in UTC. The EXIF field has
 * no timezone at all; two `DateTimeOriginal` values parsed this same way
 * yield a delta equal to the actual elapsed wall-clock time between shots,
 * which is exactly what a duration estimate needs (see file header).
 */
export function parseExifDateTimeOriginal(value: unknown): number | null {
  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) return null;
    return Date.UTC(
      value.getFullYear(),
      value.getMonth(),
      value.getDate(),
      value.getHours(),
      value.getMinutes(),
      value.getSeconds(),
    );
  }

  if (typeof value !== 'string') return null;

  const match = EXIF_DATETIME_RE.exec(value.trim());
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s));
}

/**
 * Reads `DateTimeOriginal` from `file`'s embedded EXIF and returns a
 * timezone-neutral epoch-ms number, or `null` on any failure (no EXIF
 * segment, no `DateTimeOriginal` tag, corrupt/truncated file, unsupported
 * format, parse error). Never throws — a photo lacking a usable timestamp
 * is exactly the "no suggestion" case (Locked Decision 2), not an error.
 *
 * Uses exifr's "lite" bundle (a fraction of the full build's size) via a
 * dynamic import — this is lazy-loaded only when a before/after photo is
 * actually picked (mirrors `heicTranscode.ts`'s `heic2any` dynamic import),
 * so it never touches the initial bundle. `reviveValues: false` keeps
 * `DateTimeOriginal` as the raw EXIF string instead of exifr's default
 * local-timezone `Date` coercion — see `parseExifDateTimeOriginal`'s doc for
 * why that matters here.
 *
 * IMPORTANT for HEIC: call this on the ORIGINAL picked `File`, before
 * `ensureJpeg`'s canvas-based transcode — the transcode does not preserve
 * EXIF, so reading afterward would always return `null`.
 */
export async function readExifCapturedAtMs(file: File): Promise<number | null> {
  try {
    const { default: exifr } = await import('exifr/dist/lite.esm.mjs');
    const tags = (await exifr.parse(file, { reviveValues: false })) as
      | { DateTimeOriginal?: unknown }
      | undefined;
    return parseExifDateTimeOriginal(tags?.DateTimeOriginal);
  } catch {
    return null;
  }
}

/**
 * UI-facing tri-state for the before/after suggestion affordance:
 *   - `pending`: fewer than two photos picked yet (or still reading) — show
 *     nothing, this isn't a "we tried and failed" state.
 *   - `unavailable`: both photos are present but no usable suggestion could
 *     be computed (missing EXIF on either, or an implausible delta) — a
 *     quiet, non-error note explains this; the owner enters hours manually.
 *   - `suggested`: a duration was computed; `hours` is the prefill value.
 */
export type DurationSuggestion =
  | { status: 'pending' }
  | { status: 'unavailable' }
  | { status: 'suggested'; hours: number };

/**
 * Combines two (possibly still-resolving) captured-at reads into the
 * UI-facing tri-state above. `undefined` means "no photo in that slot yet
 * / still reading"; `null` means "read completed, no usable timestamp".
 */
export function evaluateDurationSuggestion(
  beforeMs: number | null | undefined,
  afterMs: number | null | undefined,
): DurationSuggestion {
  if (beforeMs === undefined || afterMs === undefined) return { status: 'pending' };
  if (beforeMs === null || afterMs === null) return { status: 'unavailable' };
  const hours = suggestDuration(beforeMs, afterMs);
  return hours === null ? { status: 'unavailable' } : { status: 'suggested', hours };
}
